import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../../components/feature/AdminLayout';
import { supabase } from '../../../services/supabaseClient';
import { hasPermission } from '../../../constants/permissions';

interface Announcement {
  id: string;
  title: string;
  content: string;
  priority: 'urgent' | 'high' | 'normal';
  is_pinned: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  admin_profiles?: {
    name: string;
  };
}

interface RecentOrder {
  id: string;
  order_number: string;
  customer_name: string;
  customer_phone: string;
  status: string;
  created_at: string;
  order_items?: Array<{
    quantity: number;
    products?: {
      name: string;
    };
  }>;
}

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    todayOrders: 0,
    pendingOrders: 0,
    newRequests: 0,
    totalEquipment: 0
  });

  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [recentOrders, setRecentOrders] = useState<RecentOrder[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState<Announcement | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    priority: 'normal' as 'urgent' | 'high' | 'normal',
    is_pinned: false
  });
  const [adminName, setAdminName] = useState<string>('管理員');
  const [adminRole, setAdminRole] = useState<string>('');
  const [isRoleLoaded, setIsRoleLoaded] = useState(false);

  useEffect(() => {
    fetchAdminProfile();
    fetchAnnouncements();
    fetchRecentOrders();
    fetchStats();
  }, []);

  const fetchAdminProfile = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data } = await supabase
          .from('admin_profiles')
          .select('name, role')
          .eq('id', user.id)
          .maybeSingle();
        
        if (data?.name) {
          setAdminName(data.name);
        }
        if (data?.role) {
          setAdminRole(data.role);
          console.log('✅ 管理員角色載入:', data.role);
        }
      }
    } catch (error) {
      console.error('Error fetching admin profile:', error);
    } finally {
      setIsRoleLoaded(true);
    }
  };

  const fetchStats = async () => {
    try {
      // 今日訂單數
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const { count: todayCount } = await supabase
        .from('orders')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', today.toISOString());

      // 待審核訂單數
      const { count: pendingCount } = await supabase
        .from('orders')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'pending');

      // 需補資料訂單數
      const { count: needInfoCount } = await supabase
        .from('orders')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'need_info');

      // 器材總數
      const { count: equipmentCount } = await supabase
        .from('products')
        .select('*', { count: 'exact', head: true });

      setStats({
        todayOrders: todayCount || 0,
        pendingOrders: pendingCount || 0,
        newRequests: needInfoCount || 0,
        totalEquipment: equipmentCount || 0
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchRecentOrders = async () => {
    try {
      const { data, error } = await supabase
        .from('orders')
        .select(`
          id,
          order_number,
          customer_id,
          status,
          total_amount,
          created_at,
          customers (
            name,
            phone
          ),
          order_items (
            quantity,
            equipment:products!order_items_equipment_id_fkey (
              name_zh,
              name_ja
            )
          )
        `)
        .order('created_at', { ascending: false })
        .limit(5);

      if (error) {
        console.error('❌ 載入訂單失敗:', error);
        throw error;
      }

      console.log('✅ 成功載入訂單:', data);
      setRecentOrders(data || []);
    } catch (error) {
      console.error('Error fetching recent orders:', error);
    }
  };

  const fetchAnnouncements = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('announcements')
        .select('*, admin_profiles(name)')
        .order('is_pinned', { ascending: false })
        .order('priority', { ascending: true })
        .order('created_at', { ascending: false });

      if (error) throw error;
      setAnnouncements(data || []);
    } catch (error) {
      console.error('Error fetching announcements:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.title.trim() || !formData.content.trim()) return;

    try {
      if (editingAnnouncement) {
        const { error } = await supabase
          .from('announcements')
          .update({
            title: formData.title,
            content: formData.content,
            priority: formData.priority,
            is_pinned: formData.is_pinned,
            updated_at: new Date().toISOString()
          })
          .eq('id', editingAnnouncement.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('announcements')
          .insert({
            title: formData.title,
            content: formData.content,
            priority: formData.priority,
            is_pinned: formData.is_pinned
          });

        if (error) throw error;
      }

      setShowAddModal(false);
      setEditingAnnouncement(null);
      setFormData({ title: '', content: '', priority: 'normal', is_pinned: false });
      fetchAnnouncements();
    } catch (error) {
      console.error('Error saving announcement:', error);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('確定要刪除此公告嗎？')) return;

    try {
      const { error } = await supabase
        .from('announcements')
        .delete()
        .eq('id', id);

      if (error) throw error;
      fetchAnnouncements();
    } catch (error) {
      console.error('Error deleting announcement:', error);
    }
  };

  const handleTogglePin = async (announcement: Announcement) => {
    try {
      const { error } = await supabase
        .from('announcements')
        .update({ is_pinned: !announcement.is_pinned })
        .eq('id', announcement.id);

      if (error) throw error;
      fetchAnnouncements();
    } catch (error) {
      console.error('Error toggling pin:', error);
    }
  };

  const openEditModal = (announcement: Announcement) => {
    setEditingAnnouncement(announcement);
    setFormData({
      title: announcement.title,
      content: announcement.content,
      priority: announcement.priority,
      is_pinned: announcement.is_pinned
    });
    setShowAddModal(true);
  };

  const getPriorityConfig = (priority: string) => {
    const config: Record<string, { label: string; bgColor: string; textColor: string; borderColor: string }> = {
      urgent: { label: '緊急', bgColor: 'bg-red-50', textColor: 'text-red-700', borderColor: 'border-red-300' },
      high: { label: '重要', bgColor: 'bg-orange-50', textColor: 'text-orange-700', borderColor: 'border-orange-300' },
      normal: { label: '一般', bgColor: 'bg-gray-50', textColor: 'text-gray-700', borderColor: 'border-gray-200' }
    };
    return config[priority] || config.normal;
  };

  const getStatusText = (status: string) => {
    const statusMap: Record<string, string> = {
      pending: '待審核',
      approved: '已確認',
      need_info: '需補資料',
      rejected: '已拒絕',
      completed: '已完成',
      cancelled: '已取消'
    };
    return statusMap[status] || status;
  };

  const getStatusColor = (status: string) => {
    const colorMap: Record<string, string> = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      need_info: 'bg-blue-100 text-blue-800',
      rejected: 'bg-red-100 text-red-800',
      completed: 'bg-gray-100 text-gray-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };
    return colorMap[status] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('zh-TW', {
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getOrderEquipmentSummary = (order: RecentOrder) => {
    if (!order.order_items || order.order_items.length === 0) {
      return '無器材資訊';
    }

    const items = order.order_items
      .map(item => {
        const name = item.products?.name || '未知器材';
        return `${name} x${item.quantity}`;
      })
      .join(', ');

    return items.length > 50 ? items.substring(0, 50) + '...' : items;
  };

  return (
    <AdminLayout>
      <div className="px-6 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">控制台</h1>
          <p className="text-gray-600 mt-2">歡迎回來，{adminName}</p>
        </div>

        {/* Google Calendar 授權提示 - 使用 hasPermission 判斷 */}
        {isRoleLoaded && adminRole && hasPermission(adminRole, 'google_calendar', 'manage_system') && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-center justify-between">
            <div className="flex items-center gap-3">
              <i className="ri-calendar-line text-amber-600 text-2xl"></i>
              <div>
                <p className="font-medium text-gray-900">Google Calendar 同步</p>
                <p className="text-sm text-gray-600">完成授權後，訂單會自動同步到 Google Calendar</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/admin/settings/google-calendar')}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-settings-3-line mr-2"></i>
              前往設定
            </button>
          </div>
        )}

        {/* Announcement Board */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <div className="p-6 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
                <i className="ri-megaphone-line text-xl text-red-600"></i>
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">公告留言板</h2>
                <p className="text-sm text-gray-500">各店重要資訊提醒</p>
              </div>
            </div>
            <button
              onClick={() => {
                setEditingAnnouncement(null);
                setFormData({ title: '', content: '', priority: 'normal', is_pinned: false });
                setShowAddModal(true);
              }}
              className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors flex items-center gap-2 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line"></i>
              新增公告
            </button>
          </div>

          <div className="p-6 max-h-[500px] overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <i className="ri-loader-4-line text-2xl text-gray-400 animate-spin"></i>
              </div>
            ) : announcements.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <i className="ri-clipboard-line text-4xl mb-2"></i>
                <p>目前沒有公告</p>
              </div>
            ) : (
              <div className="space-y-4">
                {announcements.map((announcement) => {
                  const priorityConfig = getPriorityConfig(announcement.priority);
                  return (
                    <div
                      key={announcement.id}
                      className={`p-4 rounded-xl border-2 ${priorityConfig.bgColor} ${priorityConfig.borderColor} relative`}
                    >
                      {announcement.is_pinned && (
                        <div className="absolute -top-2 -left-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center">
                          <i className="ri-pushpin-fill text-white text-xs"></i>
                        </div>
                      )}
                      
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${priorityConfig.textColor} ${priorityConfig.bgColor} border ${priorityConfig.borderColor}`}>
                              {priorityConfig.label}
                            </span>
                            <h3 className={`font-semibold ${priorityConfig.textColor}`}>
                              {announcement.title}
                            </h3>
                          </div>
                          <div className={`text-sm whitespace-pre-wrap ${priorityConfig.textColor} opacity-90`}>
                            {announcement.content}
                          </div>
                          <div className="mt-3 text-xs text-gray-500">
                            {formatDate(announcement.created_at)}
                            {announcement.admin_profiles?.name && (
                              <span> · {announcement.admin_profiles.name}</span>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-1 shrink-0">
                          <button
                            onClick={() => handleTogglePin(announcement)}
                            className={`w-8 h-8 rounded-lg flex items-center justify-center cursor-pointer transition-colors ${
                              announcement.is_pinned 
                                ? 'bg-red-100 text-red-600 hover:bg-red-200' 
                                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                            }`}
                            title={announcement.is_pinned ? '取消置頂' : '置頂'}
                          >
                            <i className={announcement.is_pinned ? 'ri-pushpin-fill' : 'ri-pushpin-line'}></i>
                          </button>
                          <button
                            onClick={() => openEditModal(announcement)}
                            className="w-8 h-8 rounded-lg bg-gray-100 text-gray-500 hover:bg-gray-200 flex items-center justify-center cursor-pointer transition-colors"
                            title="編輯"
                          >
                            <i className="ri-edit-line"></i>
                          </button>
                          <button
                            onClick={() => handleDelete(announcement.id)}
                            className="w-8 h-8 rounded-lg bg-gray-100 text-gray-500 hover:bg-red-100 hover:text-red-600 flex items-center justify-center cursor-pointer transition-colors"
                            title="刪除"
                          >
                            <i className="ri-delete-bin-line"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div 
            onClick={() => navigate('/admin/orders')}
            className="bg-white rounded-2xl p-6 shadow-sm cursor-pointer hover:shadow-md hover:scale-[1.02] transition-all duration-200"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <i className="ri-file-list-3-line text-2xl text-blue-600"></i>
              </div>
              <span className="text-2xl font-bold text-gray-900">{stats.todayOrders}</span>
            </div>
            <h3 className="text-sm font-medium text-gray-600">今日新訂單</h3>
          </div>

          <div 
            onClick={() => navigate('/admin/orders?status=pending')}
            className="bg-white rounded-2xl p-6 shadow-sm cursor-pointer hover:shadow-md hover:scale-[1.02] transition-all duration-200"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center">
                <i className="ri-time-line text-2xl text-yellow-600"></i>
              </div>
              <span className="text-2xl font-bold text-gray-900">{stats.pendingOrders}</span>
            </div>
            <h3 className="text-sm font-medium text-gray-600">待審核訂單</h3>
          </div>

          <div 
            onClick={() => navigate('/admin/orders')}
            className="bg-white rounded-2xl p-6 shadow-sm cursor-pointer hover:shadow-md hover:scale-[1.02] transition-all duration-200"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <i className="ri-mail-line text-2xl text-green-600"></i>
              </div>
              <span className="text-2xl font-bold text-gray-900">{stats.newRequests}</span>
            </div>
            <h3 className="text-sm font-medium text-gray-600">新需求單</h3>
          </div>

          <div 
            onClick={() => navigate('/admin/equipment')}
            className="bg-white rounded-2xl p-6 shadow-sm cursor-pointer hover:shadow-md hover:scale-[1.02] transition-all duration-200"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <i className="ri-camera-line text-2xl text-purple-600"></i>
              </div>
              <span className="text-2xl font-bold text-gray-900">{stats.totalEquipment}</span>
            </div>
            <h3 className="text-sm font-medium text-gray-600">器材總數</h3>
          </div>
        </div>

        {/* Recent Orders */}
        <div className="bg-white rounded-2xl shadow-sm">
          <div className="p-6 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">最新訂單</h2>
            <button
              onClick={() => navigate('/admin/orders')}
              className="text-sm text-gray-600 hover:text-gray-900 cursor-pointer whitespace-nowrap"
            >
              查看全部 →
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    訂單編號
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    客戶
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    器材
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    狀態
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    時間
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    操作
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {recentOrders.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                      <i className="ri-inbox-line text-4xl mb-2"></i>
                      <p>目前沒有訂單</p>
                    </td>
                  </tr>
                ) : (
                  recentOrders.map((order: any) => {
                    // 組合器材清單摘要
                    const equipmentSummary = order.order_items
                      ?.map((item: any) => {
                        const name = item.equipment?.name_zh || item.equipment?.name_ja || '未知器材';
                        return `${name} x${item.quantity}`;
                      })
                      .join(', ') || '無器材資訊';

                    return (
                      <tr key={order.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {order.order_number}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{order.customer_name}</div>
                          <div className="text-xs text-gray-500">{order.customer_phone}</div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">
                          {equipmentSummary}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                            {getStatusText(order.status)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {formatDate(order.created_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <button 
                            onClick={() => navigate('/admin/orders')}
                            className="text-gray-900 hover:text-gray-700 font-medium cursor-pointer whitespace-nowrap"
                          >
                            查看詳情
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingAnnouncement ? '編輯公告' : '新增公告'}
              </h3>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setEditingAnnouncement(null);
                }}
                className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center cursor-pointer"
              >
                <i className="ri-close-line text-xl text-gray-500"></i>
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">標題</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="例如：詐騙名單注意"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">內容</label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  placeholder="輸入公告內容..."
                  rows={6}
                  maxLength={500}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none resize-none"
                />
                <p className="text-xs text-gray-400 mt-1 text-right">{formData.content.length}/500</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">優先級</label>
                  <select
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value as 'urgent' | 'high' | 'normal' })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none cursor-pointer"
                  >
                    <option value="urgent">🔴 緊急</option>
                    <option value="high">🟠 重要</option>
                    <option value="normal">⚪ 一般</option>
                  </select>
                </div>

                <div className="flex items-end">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_pinned}
                      onChange={(e) => setFormData({ ...formData, is_pinned: e.target.checked })}
                      className="w-5 h-5 rounded border-gray-300 text-gray-900 focus:ring-gray-900 cursor-pointer"
                    />
                    <span className="text-sm text-gray-700">置頂此公告</span>
                  </label>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setEditingAnnouncement(null);
                }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                取消
              </button>
              <button
                onClick={handleSubmit}
                disabled={!formData.title.trim() || !formData.content.trim()}
                className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
              >
                {editingAnnouncement ? '儲存變更' : '發布公告'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
